import { inject, injectable } from 'inversify';
import { applyToPoint, inverse } from 'transformation-matrix';
import { ZOOM_EVENTS } from '../../constants/CONTROLLERS_EVENT_TYPES';
import IDS from '../../constants/IDS';
import { createCanvas } from '../../helpers/canvas';
import { findExtremeCoordinates, getBoundingClientRect } from '../../helpers/coordinates';
import { getDpr, isMobile } from '../../helpers/device';
import { createSeat, filterSeats } from '../../helpers/seats';
import { trackTransforms } from '../../helpers/transform';
import { getWidgetParams } from '../../helpers/widget';
import { Settings } from '../../types/settings';
import { ISchemeResponse, Widget } from '../../types/widget';
import { BACKGROUND_MODULE_IDS } from './background/background.types';
import { DRAG_MODULE_IDS } from './drag/drag.module';
import { META_MODULE_IDS } from './meta/meta.module';
import { STATE_MODULE_IDS } from './state/state.types';
import { ZOOM_MODULE_IDS } from './zoom/zoom.types';
import type { IBackgroundController } from './background/background.interface';
import type { IZoomController } from './zoom/zoom.interface';
import type { IStateController } from './state/state.interface';
import { TFlags } from '../../types/flags';

@injectable()
class WidgetSkeleton implements Widget {
  uuid: string;
  rootLayout: DOMRect;
  staticSeats: Array<any> = [];
  schemeParameters: {
    minX?: number;
    minY?: number;
    maxX?: number;
    maxY?: number;
    playgroundHeight?: number;
    playgroundWidth?: number;
    schemeWidth?: number;
    schemeHeight?: number;
    startX?: number;
    startY?: number;
    isStadium?: boolean;
    zoomRange?: Array<number>;
    hallLayoutMeta?: {
      width: number;
      height: number;
      bg: string;
    };
  } = {};

  dpr: number = getDpr();
  isMobile: boolean = isMobile();
  settings: Settings;
  flags: TFlags;

  width: number;
  height: number;

  staticCanvas: HTMLCanvasElement;
  seatsCanvas: HTMLCanvasElement;

  ctxs: Array<CanvasRenderingContext2D> = [];
  staticCtx: CanvasRenderingContext2D;
  seatsCtx: CanvasRenderingContext2D;

  shouldRerenderOnLoadData: boolean = false;

  /**
   * css top of staticCanvas
   */
  offsetTop: number;

  /**
   * css left of staticCanvas
   */
  offsetLeft: number;

  dragXoffset = 0;
  dragYoffset = 0;

  @inject(ZOOM_MODULE_IDS.ZOOM_CONTROLLER) public zoomController: IZoomController;
  @inject(DRAG_MODULE_IDS.DRAG_CONTROLLER) public draggingController;
  @inject(STATE_MODULE_IDS.STATE_CONTROLLER) public stateController: IStateController;
  @inject(BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER)
  public backgroundController: IBackgroundController;
  @inject(META_MODULE_IDS.META_CONTROLLER) public metaController;

  setSettings({ uuid, settings }) {
    this.uuid = uuid;

    const isRtl = ['ar', 'he'].includes(settings.lang);
    const { lang = 'ru', ...rest } = settings;

    this.settings = {
      lang,
      ...rest,
    };

    if (isRtl) {
      document.body.setAttribute('dir', 'rtl');
    }

    if (settings.schemeMode !== undefined) {
      // this.schemeMode = settings.schemeMode;
    }
  }

  setFlags = (flags: TFlags) => {
    this.flags = flags;
  };

  get staticSeatsInViewport() {
    if (this.zoomController.zoomIsMinimum) {
      return this.staticSeats;
    }
    const { x, y, x2, y2 } = this.getViewport();
    return filterSeats({
      seats: this.staticSeats,
      x,
      y,
      x2,
      y2,
    });
  }

  getCenterOfScheme() {
    return {
      x: this.width / this.dpr / 2 + this.offsetLeft,
      y: this.height / this.dpr / 2 + this.offsetTop,
    };
  }

  setSchemeParameters = (data: ISchemeResponse) => {
    const height = this.rootLayout.height * this.dpr;
    const width = this.rootLayout.width * this.dpr;
    let minX = 0;
    let minY = 0;

    let maxX = data.width;
    let maxY = data.height;

    if (data.meta && data.meta.hall_layout) {
      maxX = Number(data.meta.hall_layout.meta.width);
      maxY = Number(data.meta.hall_layout.meta.height);
    }

    if (isNaN(maxX) || isNaN(maxY)) {
      ({ minX, minY, maxX, maxY } = findExtremeCoordinates(data.coordinates));
    }

    const { zoomRange, playgroundHeight, playgroundWidth, startX, startY, isStadium } =
      getWidgetParams({
        width,
        height,
        settings: this.settings,
        dpr: this.dpr,
        isMobile: this.isMobile,
        coords: {
          minX,
          minY,
          maxX,
          maxY,
        },
      });

    const schemeWidth = (maxX - minX) * this.dpr;
    const schemeHeight = (maxY - minY) * this.dpr;

    let adoptedMetaForNewWidget = {};

    if (window.Widget.settings.eventData) {
      const { eventData } = window.Widget.settings;

      adoptedMetaForNewWidget = {
        hall_layout: {
          meta: {
            bg: data.bg,
            width: data.width,
            height: data.height,
          },
        },
        title: eventData.title,
        bg: data.bg,
        width: data.width,
        height: data.height,
        dates: window.Widget.settings.dates,
      };
    }

    this.schemeParameters = {
      minX,
      minY,
      maxX,
      maxY,
      playgroundHeight,
      playgroundWidth,
      schemeWidth,
      schemeHeight,
      startX,
      startY,
      isStadium,
      hallLayoutMeta: {
        ...(data.meta && { ...data.meta.hall_layout.meta }),
        ...adoptedMetaForNewWidget,
      },
    };

    this.width = this.rootLayout.width * this.dpr;
    this.height = this.rootLayout.height * this.dpr;
    this.setZoomRange(zoomRange);
    this.translate(startX, startY);
    this.scale(zoomRange[0]);
  };

  setZoomRange(zoomRange: Array<number>) {
    this.zoomController.setZoomRange(zoomRange);
  }

  translate(tx, ty) {
    this.ctxs.forEach((ctx) => {
      ctx.translate(tx, ty);
    });
  }

  scale(scale) {
    this.ctxs.forEach((ctx) => {
      ctx.scale(scale, scale);
    });
  }

  createSchemeCanvas(layout: HTMLElement) {
    this.staticCanvas = createCanvas(IDS.staticCanvas);
    this.seatsCanvas = createCanvas(IDS.canvas);
    const fragment = document.createDocumentFragment();
    this.rootLayout = getBoundingClientRect(layout);

    fragment.appendChild(this.staticCanvas);
    fragment.appendChild(this.seatsCanvas);
    layout.prepend(fragment);

    const { top: offsetTop, left: offsetLeft } = getBoundingClientRect(this.staticCanvas);
    const widgetWidth = this.rootLayout.width;
    const widgetHeight = this.rootLayout.height;

    this.staticCanvas.style.width = `${widgetWidth}px`;
    this.staticCanvas.style.height = `${widgetHeight}px`;
    this.seatsCanvas.style.width = `${widgetWidth}px`;
    this.seatsCanvas.style.height = `${widgetHeight}px`;
    this.staticCanvas.setAttribute('width', `${widgetWidth * this.dpr}`);
    this.staticCanvas.setAttribute('height', `${widgetHeight * this.dpr}`);
    this.seatsCanvas.setAttribute('width', `${widgetWidth * this.dpr}`);
    this.seatsCanvas.setAttribute('height', `${widgetHeight * this.dpr}`);

    this.staticCtx = this.staticCanvas.getContext('2d');
    this.seatsCtx = this.seatsCanvas.getContext('2d');
    this.ctxs = [this.staticCtx, this.seatsCtx];
    this.offsetTop = offsetTop;
    this.offsetLeft = offsetLeft;

    trackTransforms(this.staticCtx);
    trackTransforms(this.seatsCtx);

    this.backgroundController.setBackgroundCtx(this.staticCtx);
  }

  resetStyles() {
    this.staticCanvas.style.top = '0';
    this.staticCanvas.style.left = '0';
    this.staticCanvas.style.transition = 'none';
    this.seatsCanvas.style.top = '0';
    this.seatsCanvas.style.left = '0';
    this.seatsCanvas.style.transition = 'none';
  }

  resetTransforms() {
    const { width, height } = this;

    this.ctxs.forEach((ctx) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, width, height);
    });
  }

  resize = ({ widgetWidth, widgetHeight, offsetTop, offsetLeft, isFullscreen }) => {
    if (this.stateController.state.isFailure) return;
    this.width = widgetWidth * this.dpr;
    this.height = widgetHeight * this.dpr;
    this.offsetTop = offsetTop;
    this.offsetLeft = offsetLeft;
    const { minX, minY, maxX, maxY } = this.schemeParameters;

    const { height, width } = this;

    this.staticCanvas.setAttribute('width', `${width}`);
    this.staticCanvas.setAttribute('height', `${height}`);
    this.seatsCanvas.setAttribute('width', `${width}`);
    this.seatsCanvas.setAttribute('height', `${height}`);

    const { zoomRange, playgroundHeight, playgroundWidth, startX, startY } = getWidgetParams({
      width,
      height,
      isFullscreen,
      dpr: this.dpr,
      settings: this.settings,
      isMobile: this.isMobile,
      coords: {
        minX,
        minY,
        maxX,
        maxY,
      },
    });

    this.schemeParameters = {
      ...this.schemeParameters,
      playgroundHeight,
      playgroundWidth,
      startX,
      startY,
    };

    this.setZoomRange(zoomRange);

    const [newScale] = this.zoomController.zoomRange;
    this.resetStyles();
    this.resetTransforms();
    this.translate(startX, startY);
    this.scale(newScale);
    this.update();
    this.backgroundController.forceUpdate();
    this.zoomController.emit(ZOOM_EVENTS.UPDATE);
  };

  update() {
    // TODO: need add event emmit for update static seats and tickets form common widget
  }

  renderSeatsPoints = (seats) => {
    seats.forEach((seat) => {
      createSeat({
        ctx: this.staticCtx,
        seat,
        previewMode: this.settings.previewMode,
      });
    });
  };

  moveSchemeBack({ offsetX, offsetY, diffX, diffY, onEnd }) {
    if (offsetX === diffX && offsetY === diffY) {
      onEnd();
      this.resetStyles();
      return;
    }

    const top = offsetY !== diffY ? `${offsetY}px` : this.staticCanvas.style.top;
    const left = offsetX !== diffX ? `${offsetX}px` : this.staticCanvas.style.left;

    this.staticCanvas.style.transition = 'all .1s linear';
    this.staticCanvas.style.left = left;
    this.staticCanvas.style.top = top;

    this.seatsCanvas.style.transition = 'all .1s linear';
    this.seatsCanvas.style.left = left;
    this.seatsCanvas.style.top = top;

    const transitionCallback = () => {
      this.resetStyles();
      this.staticCanvas.removeEventListener('transitionend', transitionCallback);
      onEnd();
    };

    this.staticCanvas.addEventListener('transitionend', transitionCallback);
  }

  resetStylesAfterTouch() {
    this.resetZoomStylesNode(this.staticCanvas);
    this.resetZoomStylesNode(this.seatsCanvas);
  }

  zoom({ prevScale, newZoom = null, x: xCoord, y: yCoord, onEnd }) {
    let x = xCoord - this.offsetLeft;
    let y = yCoord - this.offsetTop;
    if (this.zoomController.isZooming) return;

    const { currentZoom: newScale, zoomIsMinimum: nexZoomIsMin } = this.zoomController;
    const { startX, startY } = this.schemeParameters;
    const { dpr } = this;

    this.zoomController.startZooming();

    /* zoom to min */
    if (nexZoomIsMin) {
      // TODO fix it later
      this.ctxs.forEach((ctx) => {
        ctx.setTransform(newScale, 0, 0, newScale, startX, startY);
      });
      onEnd();
      this.zoomController.stopZooming();
      return;
    }

    if (!(this.zoomController.currentZoomIndex - 1)) {
      if (x * dpr < startX) {
        x = startX / dpr;
      }

      if (x * dpr > startX + this.schemeParameters.playgroundWidth) {
        x = (startX + this.schemeParameters.playgroundWidth) / dpr;
      }

      if (y * dpr < startY) {
        y = startY / dpr;
      }

      if (y * dpr > startY + this.schemeParameters.playgroundHeight) {
        y = (startY + this.schemeParameters.playgroundHeight) / dpr;
      }
    }

    const prevCenter = {
      x: x / prevScale,
      y: y / prevScale,
    };

    const newCenter = {
      x: x / newScale,
      y: y / newScale,
    };

    const scaleFactor = prevScale / newScale;

    const leftOffsetDelta = (newCenter.x - prevCenter.x) * this.dpr * newScale;
    const topOffsetDelta = (newCenter.y - prevCenter.y) * this.dpr * newScale;

    const transform = this.staticCtx.getTransform();

    const tx = transform.e / scaleFactor + leftOffsetDelta + (this.dragXoffset || 0) * this.dpr;
    const ty = transform.f / scaleFactor + topOffsetDelta + (this.dragYoffset || 0) * this.dpr;

    if (newZoom) {
      this.ctxs.forEach((ctx) => {
        ctx.setTransform(newScale, 0, 0, newScale, tx, ty);
      });

      onEnd();
      this.zoomController.stopZooming();
      this.dragXoffset = 0;
      this.dragYoffset = 0;

      return;
    }

    this.zoomNode({
      node: this.staticCanvas,
      x,
      y,
      scaleFactor,
    });
    this.zoomNode({
      node: this.seatsCanvas,
      x,
      y,
      scaleFactor,
    });

    let onTransitionEnd = null;

    const animationPromise = new Promise((resolve) => {
      onTransitionEnd = () => {
        this.resetZoomStylesNode(this.staticCanvas);
        this.resetZoomStylesNode(this.seatsCanvas);
        this.ctxs.forEach((ctx) => {
          ctx.setTransform(newScale, 0, 0, newScale, tx, ty);
        });

        resolve(null);
      };
      this.staticCanvas.addEventListener('transitionend', onTransitionEnd);
    });

    const promises = [];
    promises.push(animationPromise);

    Promise.all(promises).then(() => {
      onEnd();
      this.zoomController.stopZooming();
      this.staticCanvas.removeEventListener('transitionend', onTransitionEnd);
    });
  }

  zoomNode({ node, scaleFactor, x, y }) {
    const { dpr, width, height } = this;
    node.style.transition = 'all .2s linear';
    node.style.height = `${height / dpr / scaleFactor}px`;
    node.style.width = `${width / dpr / scaleFactor}px`;
    node.style.top = `${-1 * (y / scaleFactor - y)}px`;
    node.style.left = `${-1 * (x / scaleFactor - x)}px`;
  }

  resetZoomStylesNode(node) {
    const { dpr, width, height } = this;
    node.removeAttribute('style');
    node.style.transition = 'none';
    node.style.top = 0;
    node.style.left = 0;
    node.style.width = `${width / dpr}px`;
    node.style.height = `${height / dpr}px`;
  }

  pan({ offsetX = 0, offsetY = 0 }) {
    this.ctxs.forEach((ctx) => {
      const { a: scaleX, d: scaleY, e: tx, f: ty } = ctx.getTransform();
      const { dpr } = this;
      const newX = tx + offsetX * dpr;
      const newY = ty + offsetY * dpr;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.translate(newX, newY);
      ctx.scale(scaleX, scaleY);
    });
  }

  moveTo(x, y) {
    this.clear();

    this.ctxs.forEach((ctx) => {
      const { a: scaleX, d: scaleY } = ctx.getTransform();

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.translate(x, y);
      ctx.scale(scaleX, scaleY);
    });
  }

  clear() {
    const { width, height } = this;

    this.ctxs.forEach((ctx) => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.restore();
    });
  }

  clearDynamicCtx() {
    const { width, height } = this;

    this.seatsCtx.save();
    this.seatsCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.seatsCtx.clearRect(0, 0, width, height);
    this.seatsCtx.restore();
  }

  clearStaticCtx() {
    const { width, height } = this;

    this.staticCtx.save();
    this.staticCtx.setTransform(1, 0, 0, 1, 0, 0);
    this.staticCtx.clearRect(0, 0, width, height);
    this.staticCtx.restore();
  }

  transformPoint({ x, y, ctx = this.staticCtx, inverseMatrix = true }) {
    const matrix = ctx.getTransform();

    if (inverseMatrix) {
      return applyToPoint(inverse(matrix), { x, y });
    }

    return applyToPoint(matrix, { x, y });
  }

  getViewport() {
    const { width, height } = this;
    const point = this.transformPoint({ x: 0, y: 0 });
    const point2 = this.transformPoint({ x: width, y: height });

    return {
      x: point.x,
      y: point.y,
      x2: point2.x,
      y2: point2.y,
    };
  }

  getScale(ctx = this.staticCtx) {
    const { a, d } = ctx.getTransform();

    return {
      scaleX: a,
      scaleY: d,
    };
  }
}

export default WidgetSkeleton;
